import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import useCurrentUser from "@/lib/useCurrentUser";
import { professionSubjects, getProfession } from "@/lib/subjects";
import { db } from "@/api/base44Client";
import LearnTabs from "@/components/home/LearnTabs";

export default function Faecher() {
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const professionId = user?.profession || localStorage.getItem("user_profession") || "kaufmann";
  const currentProf = getProfession(professionId);
  const subjects = professionSubjects(professionId);
  const [selected, setSelected] = useState(new Set());
  const [badge, setBadge] = useState("show");

  const lapDate = new Date("2027-06-01");
  const today = new Date();
  const daysLeft = Math.ceil((lapDate - today) / (1000 * 60 * 60 * 24));

  useEffect(() => {
    const t1 = setTimeout(() => setBadge("fade"), 5000);
    const t2 = setTimeout(() => setBadge("gone"), 5600);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  const { data: allQuestions = [] } = useQuery({
    queryKey: ["questions-faecher", professionId],
    queryFn: () => db.entities.Question.list("-created_date", 2000),
  });

  const qCount = (id) => allQuestions.filter(
    (q) => q.subject === id && q.profession === professionId
  ).length;

  const toggleSelect = (id) => setSelected((prev) => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  const startMulti = () => {
    const ids = [...selected];
    navigate(`/quiz/${ids[0]}/start?count=10&timer=Aus&own=0&extra=${ids.slice(1).join(",")}`);
  };

  return (
    <div className="relative">
      <LearnTabs />

      <div className="flex items-center justify-between gap-2 min-h-[30px]">
        <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">LAP-VORBEREITUNG</p>
        {badge !== "gone" && (
          <div className={`bg-[#FDFFB6] border-2 border-black px-2.5 py-1 rounded-lg text-xs font-extrabold text-black shadow-sm flex items-center gap-1 transition-opacity duration-500 ${badge === "fade" ? "opacity-0" : "opacity-100"}`}>
            <span>⏳</span> LAP 2027: {daysLeft} Tage
          </div>
        )}
      </div>

      <h1 className="font-heading text-2xl sm:text-4xl font-extrabold">
        Hoi {user?.full_name || user?.name || "Admin"}.<br />Was lernst du heute?
      </h1>

      <div className="flex items-center gap-2 mt-3">
        <span className="bg-white border-2 border-black text-black px-3 py-1 rounded-lg text-xs font-bold flex items-center gap-1.5">
          <span>{currentProf?.emoji || "📊"}</span>
          <span>{currentProf?.name || "Kaufmann/-frau EFZ"}</span>
        </span>
        <button onClick={() => navigate("/berufe")}
          className="bg-slate-800 text-white px-3 py-1 rounded-lg text-xs font-bold hover:bg-black transition-colors cursor-pointer">
          Beruf ändern
        </button>
      </div>

      <p className="text-[11px] text-slate-500 font-semibold mt-3">
        Klicke auf ein Fach zum Auswählen. Bei mehreren Fächern erscheint ein Start-Button.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
        {subjects.map((s) => (
          <div key={s.id}
            onClick={() => toggleSelect(s.id)}
            className={`brutal border-2 border-black p-4 rounded-xl flex justify-between items-center text-black cursor-pointer transition-colors ${selected.has(s.id) ? "bg-[#CAFFBF]" : "bg-white"}`}>
            <div>
              <div className="text-lg font-black flex items-center gap-2">
                <span>{s.emoji}</span>
                <span>{s.name}</span>
              </div>
              <div className="text-xs text-slate-500 font-semibold mt-0.5">
                {qCount(s.id)} Fragen verfügbar
              </div>
            </div>
            <button
              onClick={(e) => { e.stopPropagation(); navigate(`/quiz/${s.id}`); }}
              className="bg-[#CAFFBF] border-2 border-black font-extrabold text-xs px-3 py-2 rounded-lg hover:bg-[#a0f58c] cursor-pointer">
              Starten →
            </button>
          </div>
        ))}
      </div>

      {selected.size > 1 && (
        <div className="fixed bottom-24 left-0 right-0 px-4 z-30 flex justify-center">
          <button onClick={startMulti}
            className="brutal-btn bg-[#CAFFBF] border-2 border-black px-6 py-3 font-extrabold text-sm shadow-[4px_4px_0_0_#000]">
            ▶ {selected.size} Fächer starten ({[...selected].reduce((a, id) => a + qCount(id), 0)} Fragen)
          </button>
        </div>
      )}
    </div>
  );
}
